import { useMemo } from "react";
import { LatLng, OrderSummary } from "@app/domain";
import { MapMarker } from "../MapView.types";
import { buildDriverMarker, buildRouteMarkers } from "../utils/buildOrderMapMarkers";

/**
 * Public hook feature code calls. Route markers (pickup/dropoff) are
 * memoized on `order` alone, so they keep their object identity across
 * driver location polls — only the driver marker is recomputed when
 * `driverLocation` changes. The combined array is still a new reference
 * every time the driver moves (adapters need that to update the driver's
 * icon), but each *individual* route marker isn't, which is what stops
 * every pickup/dropoff icon on the map from being torn down and rebuilt
 * every time the driver's position ticks.
 */
export function useOrderMapMarkers(
  order: OrderSummary,
  driverLocation: LatLng | null,
  isDropoffTracking = false
): MapMarker[] {
  const routeMarkers = useMemo(() => buildRouteMarkers(order, isDropoffTracking), [order, isDropoffTracking]);
  const driverMarker = useMemo(() => buildDriverMarker(order, driverLocation), [order, driverLocation]);

  return useMemo(
    () => (driverMarker ? [...routeMarkers, driverMarker] : routeMarkers),
    [routeMarkers, driverMarker]
  );
}
